import { Alert, AlertTitle, Button, Grid2 as Grid, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'
import { useMutation, useQuery } from 'urql'
import { NameWithFavicon } from '../../components/NameWithFavicon'
import PageHeader from '../../components/PageHeader'
import { ProjectSelect } from '../../components/ProjectSelect'
import { graphql } from '../../gql'
import { CreateRfqInput } from '../../gql/graphql'

interface IFormInput {
  name: string
  dateReceived?: Date | null
  vendorId: number | null
  projectId: number | null
}

const createMutation = graphql(/* GraphQL */ `
    mutation AddRfqPageCreateMuation($input: CreateRfqInput!) {
      CreateRfq(input: $input) {
        id
      }
    }
`)

export default function AddRFQPage() {
  const [result, createRfq] = useMutation<any>(createMutation)
  const { fetching, error } = result
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [pageQueryResult] = useQuery({
    query: graphql(/* GraphQL */ `
    query AddRfqPageQuery {
      Projects {
        id
        name
        client {
          name
          website
        }
      }
      Vendors {
        id
        name
        website
      }
    }
  `),
  })

  const queryProjectId = useMemo(() => {
    const v = searchParams.get('project')
    if (!v) return null
    return Number(v)
  }, [searchParams])

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    if (fetching) return
    const input: CreateRfqInput = {
      dateReceived: data.dateReceived ? data.dateReceived.toISOString() : null,
      vendorId: Number(data.vendorId),
      projectId: Number(data.projectId),
      name: data.name,
    }

    const response = await createRfq({ input })
    if (response.error) {
      alert(response.error)
      return
    }
    if (response.data?.CreateRfq?.id) {
      navigate(`/order/${response.data.CreateRfq.id}`)
    } else {
      alert('Error creating RFQ')
    }
  }
  const { handleSubmit, control, reset } = useForm<IFormInput>({
    defaultValues: {
      name: '',
      dateReceived: null,
      vendorId: null,
      projectId: queryProjectId || null,
    },
  })

  const $error = useMemo(() => {
    const hasError = !!error
    if (hasError) {
      return (
        <Alert severity="error">
          <AlertTitle>An error occurred</AlertTitle>
          {error.message}
        </Alert>
      )
    }
  }, [error])
  return (
    <Stack spacing={2} flex="1" component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader title="Add an RFQ" />
      <Typography variant="body2" maxWidth="sm">
        An <abbr title="Request for Quote">RFQ</abbr> is a request for quote. It is a request for a vendor to provide a
        quote for a project. Quotes, Purchase Orders, and Payments are all linked to an RFQ.
      </Typography>
      <Grid container spacing={10} pt={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={4}>
            {$error}
            <Controller
              name="projectId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                const opts = pageQueryResult.data?.Projects
                return (
                  <ProjectSelect
                    id="project-select"
                    fullWidth
                    disabled={field.disabled}
                    options={opts}
                    textFieldProps={{
                      ref: field.ref,
                      name: field.name,
                      error: !!fieldState.error,
                      helperText: fieldState.error && 'Required',
                      required: true,
                      variant: 'standard',
                    }}
                    value={opts ? opts.find((p) => p.id === field.value) || null : null}
                    onChange={(e, value) => field.onChange(value?.id || null)}
                    onBlur={field.onBlur}
                  />
                )
              }}
            />
            <Controller
              name="vendorId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  select
                  required
                  margin="dense"
                  label="Vendor"
                  fullWidth
                  variant="standard"
                  {...field}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  disabled={fetching}
                  error={!!fieldState.error}
                  helperText={fieldState.error && 'Required'}
                >
                  <MenuItem disabled>Select a vendor</MenuItem>
                  {pageQueryResult.data?.Vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      <NameWithFavicon name={vendor.name} website={vendor.website} />
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    label="RFQ Name"
                    {...field}
                    error={!!fieldState.error}
                    placeholder={`"Common area rugs" or "King double suite mirrors"...`}
                    helperText={fieldState.error?.message || 'A name for the RFQ'}
                  />
                )
              }}
            />
            <Controller
              name="dateReceived"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <DatePicker
                    label="Date Received"
                    {...field}
                    disabled={fetching}
                    disableFuture
                    slotProps={{
                      textField: {
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message || undefined,
                      },
                      field: {
                        clearable: true,
                      },
                    }}
                  />
                )
              }}
            />
            <Button type="submit" variant="contained" color="secondary" sx={{ alignSelf: 'flex-end' }}>
              Create
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  )
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
